import React, { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import {
  Briefcase, FileText, Receipt, MapPin, CalendarBlank, CheckCircle, Clock,
  Phone, EnvelopeSimple, CurrencyDollar, WarningCircle, FileArrowDown, Images, X,
} from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = "adpw_portal_token";
const todayStr = () => new Date().toISOString().slice(0, 10);
const fmtDate = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "Not scheduled");
const labelize = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

const STATUS_COLOR = {
  scheduled: "bg-indigo-100 text-indigo-700", in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700", unscheduled: "bg-slate-100 text-slate-600",
  cancelled: "bg-red-100 text-red-700", sent: "bg-sky-100 text-sky-700",
  approved: "bg-emerald-100 text-emerald-700", rejected: "bg-red-100 text-red-700",
  converted: "bg-emerald-100 text-emerald-700", draft: "bg-slate-100 text-slate-600",
  paid: "bg-emerald-100 text-emerald-700", overdue: "bg-red-100 text-red-700",
};
const Badge = ({ s }) => <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLOR[s] || "bg-slate-100 text-slate-600"}`}>{labelize(s)}</span>;

export default function Portal() {
  const { token: routeToken } = useParams();
  const stored = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  const token = routeToken || stored;

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    if (routeToken) localStorage.setItem(TOKEN_KEY, routeToken);
    api.getPortal(token)
      .then(setData)
      .catch((e) => {
        setError(e.response?.data?.detail || "We couldn't load your portal.");
        if (e.response?.status === 404) localStorage.removeItem(TOKEN_KEY);
      })
      .finally(() => setLoading(false));
  }, [token, routeToken]);

  // /portal with a saved token → go to the tokenized URL
  if (!routeToken && stored) return <Navigate to={`/portal/${stored}`} replace />;

  if (!token) return <Shell><Empty title="No portal link found" msg="Please open the link we sent you by email or text to view your account." /></Shell>;
  if (loading) return <Shell><div className="text-center text-slate-400 py-24">Loading your portal…</div></Shell>;
  if (error) return <Shell><Empty title="Link not available" msg={error} /></Shell>;

  const { client, company, jobs, quotes, invoices } = data;
  const today = todayStr();
  const upcoming = jobs.filter((j) => (j.scheduled_date && j.scheduled_date >= today && j.status !== "completed" && j.status !== "cancelled") || j.status === "in_progress");
  const past = jobs.filter((j) => !upcoming.includes(j));
  const pendingQuotes = quotes.filter((q) => q.status === "sent");
  const otherQuotes = quotes.filter((q) => q.status !== "sent");
  const unpaid = invoices.filter((i) => i.status !== "paid");
  const paid = invoices.filter((i) => i.status === "paid");

  const payNow = async (invId) => {
    setPaying(invId);
    try {
      const res = await api.checkout(invId);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start payment");
      setPaying(null);
    }
  };

  return (
    <Shell company={company}>
      {/* Greeting */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900">Welcome back, {client.name.split(" ")[0]}</h1>
        <p className="text-slate-500 mt-1 text-sm">Here's everything happening with your service.</p>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
        <Stat icon={CalendarBlank} label="Upcoming" value={upcoming.length} />
        <Stat icon={FileText} label="Quotes to review" value={pendingQuotes.length} accent={pendingQuotes.length > 0} />
        <Stat icon={Receipt} label="Unpaid invoices" value={unpaid.length} accent={unpaid.length > 0} />
        <Stat icon={MapPin} label="Properties" value={client.properties.length} />
      </div>

      {/* Quotes to review */}
      {pendingQuotes.length > 0 && (
        <Section icon={FileText} title="Quotes awaiting your approval" testid="portal-pending-quotes">
          {pendingQuotes.map((q) => (
            <Card key={q.id} testid={`portal-quote-${q.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{q.number}</span><Badge s={q.status} /></div>
                {q.property_address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={13} weight="duotone" />{q.property_address}</div>}
                <div className="text-sm font-semibold text-slate-700 mt-1">{money(q.total)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={api.quotePdfUrl(q.id)} target="_blank" rel="noreferrer" data-testid={`portal-quote-pdf-${q.id}`}
                  className="text-slate-500 hover:text-[#00B4D8] text-sm font-medium flex items-center gap-1 transition-colors">
                  <FileArrowDown size={16} weight="duotone" /> PDF
                </a>
                <a href={`/approve/${q.id}`} data-testid={`portal-approve-${q.id}`}
                  className="bg-[#00B4D8] hover:bg-cyan-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                  Review &amp; Approve
                </a>
              </div>
            </Card>
          ))}
        </Section>
      )}

      {/* Unpaid invoices */}
      {unpaid.length > 0 && (
        <Section icon={Receipt} title="Invoices due" testid="portal-unpaid-invoices">
          {unpaid.map((i) => (
            <Card key={i.id} testid={`portal-invoice-${i.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{i.number}</span><Badge s={i.status} /></div>
                {i.property_address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={13} weight="duotone" />{i.property_address}</div>}
                <div className="text-lg font-bold text-slate-900 mt-1">{money(i.total)}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={api.invoicePdfUrl(i.id)} target="_blank" rel="noreferrer" data-testid={`portal-invoice-pdf-${i.id}`}
                  className="text-slate-500 hover:text-[#00B4D8] text-sm font-medium flex items-center gap-1 transition-colors">
                  <FileArrowDown size={16} weight="duotone" /> PDF
                </a>
                <button onClick={() => payNow(i.id)} disabled={paying === i.id} data-testid={`portal-pay-${i.id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5">
                  <CurrencyDollar size={16} weight="bold" />{paying === i.id ? "Redirecting…" : "Pay now"}
                </button>
              </div>
            </Card>
          ))}
        </Section>
      )}

      {/* Upcoming jobs */}
      <Section icon={Briefcase} title="Upcoming & active work" testid="portal-upcoming-jobs">
        {upcoming.length === 0 ? <Muted>No upcoming jobs scheduled right now.</Muted> : upcoming.map((j) => <JobRow key={j.id} j={j} onPhoto={setLightbox} />)}
      </Section>

      {/* History */}
      {past.length > 0 && (
        <Section icon={CheckCircle} title="Service history" testid="portal-past-jobs">
          {past.map((j) => <JobRow key={j.id} j={j} muted onPhoto={setLightbox} />)}
        </Section>
      )}

      {/* Other quotes / paid invoices */}
      {otherQuotes.length > 0 && (
        <Section icon={FileText} title="Past quotes">
          {otherQuotes.map((q) => (
            <Card key={q.id}>
              <div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{q.number}</span><Badge s={q.status} /></div>
                {q.property_address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={13} weight="duotone" />{q.property_address}</div>}</div>
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-slate-700">{money(q.total)}</div>
                <a href={api.quotePdfUrl(q.id)} target="_blank" rel="noreferrer" data-testid={`portal-quote-pdf-${q.id}`}
                  className="text-slate-500 hover:text-[#00B4D8] text-sm font-medium flex items-center gap-1 transition-colors">
                  <FileArrowDown size={16} weight="duotone" /> PDF
                </a>
              </div>
            </Card>
          ))}
        </Section>
      )}
      {paid.length > 0 && (
        <Section icon={Receipt} title="Paid invoices">
          {paid.map((i) => (
            <Card key={i.id}>
              <div className="flex-1 min-w-0"><div className="flex items-center gap-2"><span className="font-semibold text-slate-800">{i.number}</span><Badge s="paid" /></div>
                {i.property_address && <div className="text-xs text-slate-500 flex items-center gap-1 mt-1"><MapPin size={13} weight="duotone" />{i.property_address}</div>}</div>
              <div className="flex items-center gap-3">
                <div className="text-sm font-semibold text-slate-700">{money(i.total)}</div>
                <a href={api.invoicePdfUrl(i.id)} target="_blank" rel="noreferrer" data-testid={`portal-invoice-pdf-${i.id}`}
                  className="text-slate-500 hover:text-[#00B4D8] text-sm font-medium flex items-center gap-1 transition-colors">
                  <FileArrowDown size={16} weight="duotone" /> PDF
                </a>
              </div>
            </Card>
          ))}
        </Section>
      )}
      {lightbox && <Lightbox url={lightbox} onClose={() => setLightbox(null)} />}
    </Shell>
  );
}

function Shell({ children, company }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B1726]">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <img src="/adpw-logo.png" alt="All Day Power Washing" className="h-14 w-auto" />
          {company && (
            <div className="text-right text-slate-300 text-xs space-y-0.5">
              {company.phone && <div className="flex items-center justify-end gap-1.5"><Phone size={13} weight="duotone" />{company.phone}</div>}
              {company.email && <div className="flex items-center justify-end gap-1.5"><EnvelopeSimple size={13} weight="duotone" />{company.email}</div>}
            </div>
          )}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8">{children}</main>
      <footer className="text-center text-xs text-slate-400 py-8">All Day Power Washing · Your customer portal<br /><a href="tel:+17196446105" className="text-[#00B4D8] hover:underline">(719) 644-6105</a></footer>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, accent }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-[#00B4D8]/40 bg-cyan-50/40" : "border-slate-200 bg-white"}`}>
    <Icon size={20} weight="duotone" className={accent ? "text-[#00B4D8]" : "text-slate-400"} />
    <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
    <div className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</div>
  </div>
);

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="mb-8" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3"><Icon size={18} weight="duotone" className="text-[#00B4D8]" /><h2 className="font-display font-bold text-slate-800">{title}</h2></div>
    <div className="space-y-2.5">{children}</div>
  </div>
);

const Card = ({ children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm" data-testid={testid}>{children}</div>
);

const Muted = ({ children }) => <div className="text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-xl p-4">{children}</div>;

const JobRow = ({ j, muted, onPhoto }) => {
  const photos = j.photos || [];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={`portal-job-${j.id}`}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${muted ? "bg-slate-100" : "bg-[#0B1726]"}`}>
          {j.status === "completed" ? <CheckCircle size={20} weight="duotone" className="text-emerald-500" /> : <Clock size={20} weight="duotone" className={muted ? "text-slate-400" : "text-[#00B4D8]"} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2"><span className="font-semibold text-slate-800 truncate">{j.title}</span><Badge s={j.status} /></div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
            <span className="flex items-center gap-1"><CalendarBlank size={13} weight="duotone" />{fmtDate(j.scheduled_date)}{j.scheduled_time ? ` · ${j.scheduled_time}` : ""}</span>
            {j.property_address && <span className="flex items-center gap-1"><MapPin size={13} weight="duotone" />{j.property_address}</span>}
          </div>
        </div>
      </div>
      {photos.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`portal-job-photos-${j.id}`}>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
            <Images size={14} weight="duotone" className="text-[#00B4D8]" /> {photos.length} photo{photos.length > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {photos.map((p) => (
              <button key={p.id} onClick={() => onPhoto(`${BACKEND}${p.url}`)} data-testid={`portal-photo-${p.id}`}
                className="shrink-0 rounded-lg overflow-hidden border border-slate-200 hover:border-[#00B4D8] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00B4D8]/40">
                <img src={`${BACKEND}${p.url}`} alt={p.caption || "Job photo"} loading="lazy"
                  className="h-20 w-20 object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

function Lightbox({ url, onClose }) {
  return (
    <div onClick={onClose} data-testid="portal-lightbox"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{ animation: "fadeIn .15s ease-out" }}>
      <button onClick={onClose} data-testid="portal-lightbox-close"
        className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 rounded-full p-2 transition-colors">
        <X size={22} weight="bold" />
      </button>
      <img src={url} alt="Job photo" onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-full rounded-lg shadow-2xl object-contain" />
    </div>
  );
}

const Empty = ({ title, msg }) => (
  <div className="text-center py-20">
    <WarningCircle size={44} weight="duotone" className="text-slate-300 mx-auto mb-3" />
    <h2 className="font-display text-xl font-bold text-slate-800">{title}</h2>
    <p className="text-slate-500 text-sm mt-2 max-w-md mx-auto">{msg}</p>
  </div>
);
